// "Copy response" for MW assistant replies.
//
// The reply goes on the clipboard twice: as HTML, so pasting into Word or
// Outlook keeps bold, italics, lists, headings and tables, and as plain
// Markdown for editors that only accept text. Word ignores stylesheets on
// pasted fragments, so the bits of chat.scss that matter (table borders,
// header shading, blockquote rule, code font) are inlined here.

import { renderMarkdown } from "./triloChatMarkdown";
import { DOC_REFERENCE_PATTERN } from "./triloChatConstants";

// Mirrors scss/variables.scss: $ui-lines, $background-two,
// $sidebar-primary-teal, $sidebar-light-gray.
const UI_LINES = "#dce0e5";
const BACKGROUND_TWO = "#f0f3f4";
const PRIMARY_TEAL = "#4ab8aa";
const LIGHT_GRAY = "#4b5563";

const INLINE_STYLES = {
  table: "border-collapse:collapse;width:100%;margin:10px 0;",
  th: `border:1px solid ${UI_LINES};padding:6px 10px;text-align:left;background:${BACKGROUND_TWO};font-weight:bold;`,
  td: `border:1px solid ${UI_LINES};padding:6px 10px;text-align:left;`,
  blockquote: `border-left:3px solid ${PRIMARY_TEAL};margin:8px 0;padding:4px 12px;color:${LIGHT_GRAY};`,
  code: `font-family:Consolas,'Courier New',monospace;background:${BACKGROUND_TWO};`,
  hr: `border:none;border-top:1px solid ${UI_LINES};`,
};

// `[DOC:<id>]` markers become interactive panels in the chat; they have no
// meaning outside it, so they are dropped from the copy.
const stripDocReferences = content =>
  content
    .replace(new RegExp(DOC_REFERENCE_PATTERN, "g"), "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const addInlineStyles = html =>
  Object.entries(INLINE_STYLES).reduce(
    (out, [tag, style]) =>
      out.replace(new RegExp(`<${tag}>`, "g"), `<${tag} style="${style}">`),
    html
  );

export const buildCopyText = content => stripDocReferences(content);

export const buildCopyHtml = content =>
  addInlineStyles(renderMarkdown(stripDocReferences(content)));

// Fallback for browsers without ClipboardItem (and non-secure contexts):
// selecting rendered HTML and running the legacy copy command still puts
// rich text on the clipboard.
const legacyCopyHtml = html => {
  const holder = document.createElement("div");
  holder.innerHTML = html;
  holder.setAttribute("contenteditable", "true");
  holder.style.position = "fixed";
  holder.style.left = "-9999px";
  document.body.appendChild(holder);

  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(holder);
  selection.removeAllRanges();
  selection.addRange(range);
  try {
    return document.execCommand("copy");
  } finally {
    selection.removeAllRanges();
    document.body.removeChild(holder);
  }
};

/**
 * Copy an assistant reply to the clipboard as rich text with a plain-text
 * alternative. Resolves to true on success.
 */
export async function copyResponse(content) {
  const html = buildCopyHtml(content);
  const text = buildCopyText(content);

  if (
    navigator.clipboard?.write &&
    typeof window.ClipboardItem === "function"
  ) {
    try {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
      return true;
    } catch {
      // Permission denied or unsupported MIME type - try the fallbacks.
    }
  }
  try {
    if (legacyCopyHtml(html)) return true;
  } catch {
    // execCommand unavailable - fall through to plain text.
  }
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}
